// ------------------------------------------------------------
// AI Attribution — per Loren Data AI Use Policy §8.2
// Tool:        Claude Code (Anthropic)
// 2026-07-15: Added Convert to Contract button + confirm dialog in the tenant sheet (WI9896 Phase 4) - SangPham
// ------------------------------------------------------------
/**
 * DeveloperPlanPage - Sales page for managing Developer plan tenants
 *
 * Features:
 * - DataTable listing all Developer plan tenants with billing status
 * - Client-side filters: search by name, filter by billing status
 * - Sheet panel for editing trial extension (Trial status only) and one-way
 *   conversion to a long-term contract
 *
 * Layout follows PortalLayout pattern (header, sidebar, footer).
 * Uses CQRS pattern with mediator for data fetching and updates.
 */

import type { DeveloperPlanTenant } from '@/application/queries/developerPlan';
import { PortalLayout } from '@/components/common/PortalLayout';
import {
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { convertToCsv, downloadCsv, type CsvColumn } from '@/utils/csv-export';
import { formatDateByPrefs } from '@utils/format-locale';
import { useUserPreferences } from '@/components/features/settings/UserSettings';
import { useDeveloperPlan } from './DeveloperPlanPage.hook';
import { useDeveloperPlanStore } from './DeveloperPlanPage.store';
import { RegistrationSessionsTab } from './RegistrationSessionsTab';
import { SimplifyDeveloperPlansTab } from './SimplifyDeveloperPlansTab';

// ============================================================================
// Loading Component
// ============================================================================

interface LoadingStateProps {
  loadingText: string;
}

const LoadingState: React.FC<LoadingStateProps> = ({ loadingText }) => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground">{loadingText}</p>
    </div>
  </div>
);

// ============================================================================
// Error Component
// ============================================================================

interface ErrorStateProps {
  error: Error;
  onRetry: () => void;
  errorTitle: string;
  retryText: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry, errorTitle, retryText }) => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4 p-6 max-w-md text-center">
      <div className="text-destructive text-4xl">⚠️</div>
      <h2 className="text-xl font-semibold">{errorTitle}</h2>
      <p className="text-muted-foreground">{error.message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        {retryText}
      </button>
    </div>
  </div>
);

// ============================================================================
// Trial Extension Sheet
// ============================================================================

interface TrialExtensionSheetProps {
  tenant: DeveloperPlanTenant | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExtend: (networkId: number, newDate: string) => Promise<void>;
  onConvert: (networkId: number) => Promise<void>;
}

// Helper: format a Date to YYYY-MM-DD for HTML date inputs (UTC date)
function toInputDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

// Helper: add N days to a date string (ISO or date-only), return new Date
function addDays(dateStr: string | null, days: number): Date {
  const base = dateStr ? new Date(dateStr) : new Date();
  const result = new Date(base);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

const TrialExtensionSheet: React.FC<TrialExtensionSheetProps> = ({
  tenant,
  open,
  onOpenChange,
  onExtend,
  onConvert,
}) => {
  const extendError = useDeveloperPlanStore((s) => s.extendError);
  const extendSuccess = useDeveloperPlanStore((s) => s.extendSuccess);
  const convertError = useDeveloperPlanStore((s) => s.convertError);
  const { t } = useTranslation(NS.DEVELOPER_PLAN);
  const { dateFormat, timezoneOffset } = useUserPreferences();
  const [newDate, setNewDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const handleConvertConfirm = async () => {
    if (!tenant) return;
    setIsConverting(true);
    try {
      await onConvert(tenant.networkId);
      setConfirmOpen(false);
    } finally {
      setIsConverting(false);
    }
  };

  // Compute min/max from current trial end date
  const minDate = toInputDate(addDays(tenant?.expireDate ?? null, 1));
  const maxDate = toInputDate(addDays(tenant?.expireDate ?? null, 30));

  // Preview: days being added relative to current trial end
  const previewDays = (() => {
    if (!newDate || !tenant?.expireDate) return null;
    const current = new Date(tenant.expireDate);
    const selected = new Date(newDate);
    const diff = Math.round((selected.getTime() - current.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : null;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant || !newDate) return;
    setIsSubmitting(true);
    try {
      await onExtend(tenant.networkId, newDate);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isTrialStatus = tenant?.billingStatus?.toLowerCase() === 'trialing' ||
    tenant?.billingStatus?.toLowerCase() === 'trial';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>
            {t('DeveloperPlan_Sheet_Title', 'Tenant Details')}
          </SheetTitle>
        </SheetHeader>

        {tenant && (
          <div className="mt-6 space-y-6">
            {/* Tenant Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t('DeveloperPlan_Sheet_Info', 'Information')}
              </h3>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <span className="text-muted-foreground">{t('DeveloperPlan_Col_Tenant', 'Tenant')}</span>
                <span className="font-medium">{tenant.tenant}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_NetworkId', 'Network ID')}</span>
                <span className="font-medium">{tenant.networkId}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_NetworkName', 'Network Name')}</span>
                <span className="font-medium">{tenant.networkName}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_Status', 'Billing Status')}</span>
                <span className="font-medium">{tenant.billingStatus}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_Product', 'Product')}</span>
                <span className="font-medium">{tenant.productName || '—'}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Sheet_TrialExpires', 'End Date')}</span>
                <span className="font-medium">{formatDateByPrefs(tenant.expireDate, dateFormat, timezoneOffset)}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Sheet_BeginDate', 'Begin Date')}</span>
                <span className="font-medium">{formatDateByPrefs(tenant.beginDate, dateFormat, timezoneOffset)}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_ContactName', 'Contact Name')}</span>
                <span className="font-medium">{tenant.contactName || '—'}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_ContactEmail', 'Contact Email')}</span>
                <span className="font-medium break-all">
                  {tenant.contactEmail
                    ? <a href={`mailto:${tenant.contactEmail}`} className="text-primary hover:underline">{tenant.contactEmail}</a>
                    : '—'}
                </span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_ReferralCode', 'Referral Code')}</span>
                <span className="font-medium">{tenant.referralCode || '—'}</span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_Kcs', 'KCs')}</span>
                <span className="font-medium">
                  {tenant.maxKcs
                    ? `${tenant.kcs ?? 0} / ${tenant.maxKcs}`
                    : `${tenant.kcs ?? 0}`}
                </span>

                <span className="text-muted-foreground">{t('DeveloperPlan_Col_Mailboxes', 'Mailboxes')}</span>
                <span className="font-medium">
                  {tenant.maxMailboxes
                    ? `${tenant.mailboxCreatedCount ?? 0} / ${tenant.maxMailboxes}`
                    : `${tenant.mailboxCreatedCount ?? 0}`}
                </span>
              </div>
            </div>

            {/* Extend Trial Form — only for Trial status */}
            {isTrialStatus && (
              <div className="space-y-3 border-t pt-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  {t('DeveloperPlan_Sheet_ExtendTitle', 'Extend Trial Period')}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="newTrialEndDate"
                      className="block text-sm font-medium mb-1"
                    >
                      {t('DeveloperPlan_Sheet_NewDate', 'New Trial End Date')}
                    </label>
                    <input
                      id="newTrialEndDate"
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      min={minDate}
                      max={maxDate}
                      required
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {/* Preview: days being extended */}
                    {previewDays !== null && (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {t('DeveloperPlan_Sheet_ExtendPreview', 'Extending by {{days}} day(s)').replace('{{days}}', String(previewDays))}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('DeveloperPlan_Sheet_ExtendRange', 'Select a date between 1 and 30 days from the current trial end.')}
                    </p>
                  </div>

                  {extendError && (
                    <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                      {extendError.message}
                    </div>
                  )}

                  {extendSuccess && (
                    <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm">
                      {t('DeveloperPlan_Sheet_Success', 'Trial period extended successfully!')}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || !newDate}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting && (
                      <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    )}
                    {isSubmitting
                      ? t('DeveloperPlan_Sheet_Saving', 'Saving...')
                      : t('DeveloperPlan_Sheet_Save', 'Extend Trial')}
                  </button>
                </form>
              </div>
            )}

            {/* Convert to Contract — one-way action, available regardless of billing status */}
            <div className="space-y-3 border-t pt-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t('DeveloperPlan_Sheet_ConvertTitle', 'Long-Term Contract')}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t(
                  'DeveloperPlan_Sheet_ConvertDescription',
                  'Convert this network to a long-term contract. This removes it from the Developer Plan list, cancels the developer Stripe subscription, and cannot be undone from this screen.'
                )}
              </p>

              {convertError && (
                <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  {convertError.message}
                </div>
              )}

              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                className="w-full px-4 py-2 border border-destructive text-destructive rounded-md hover:bg-destructive/10 transition-colors"
              >
                {t('DeveloperPlan_Sheet_ConvertButton', 'Convert to Long-Term Contract')}
              </button>
            </div>
          </div>
        )}
      </SheetContent>

      {/* Confirm dialog — one-way action */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('DeveloperPlan_ConvertDialog_Title', 'Convert to Long-Term Contract?')}</DialogTitle>
            <DialogDescription>
              {t(
                'DeveloperPlan_ConvertDialog_Description',
                'This action cannot be undone from this screen. Network {{networkId}} will be removed from the Developer Plan list and its developer Stripe subscription will be cancelled.'
              ).replace('{{networkId}}', String(tenant?.networkId ?? ''))}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={isConverting}
              className="px-4 py-2 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50"
            >
              {t('DeveloperPlan_ConvertDialog_Cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={handleConvertConfirm}
              disabled={isConverting}
              className="px-4 py-2 text-sm bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 disabled:opacity-50 flex items-center gap-2"
            >
              {isConverting && (
                <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              {isConverting
                ? t('DeveloperPlan_ConvertDialog_Converting', 'Converting...')
                : t('DeveloperPlan_ConvertDialog_Confirm', 'Convert')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const DeveloperPlanPage = React.forwardRef<HTMLDivElement>((_, ref) => {
  const { t } = useTranslation(NS.DEVELOPER_PLAN);

  // Fetch data using hook
  const { data, loading, error, refetch, extendTrial, syncError, syncData, convertToContract } =
    useDeveloperPlan();
  const { dateFormat, timezoneOffset } = useUserPreferences();

  // Store actions
  const selectedTenant = useDeveloperPlanStore((s) => s.selectedTenant);
  const sheetOpen = useDeveloperPlanStore((s) => s.sheetOpen);
  const openSheet = useDeveloperPlanStore((s) => s.openSheet);
  const closeSheet = useDeveloperPlanStore((s) => s.closeSheet);

  // Local syncing state — useState for reliable UI reactivity (Zustand store
  // updates may not trigger re-renders for ephemeral loading states, see Known Issues)
  const [isSyncing, setIsSyncing] = useState(false);
  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      await syncData();
    } finally {
      setIsSyncing(false);
    }
  }, [syncData]);

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Status badge colors — stable reference to avoid useMemo invalidation
  const statusColors = useMemo<Record<string, string>>(() => ({
    trialing: 'bg-yellow-100 text-yellow-800',
    trial: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    paid: 'bg-green-100 text-green-800',
    blocked: 'bg-red-100 text-red-800',
    suspended: 'bg-red-100 text-red-800',
    demo: 'bg-purple-100 text-purple-800',
    exempt: 'bg-blue-100 text-blue-800',
    unknown: 'bg-gray-100 text-gray-800',
    error: 'bg-red-100 text-red-800',
  }), []);

  // Format date for display — applies user date format preference
  const formatDate = useCallback((dateStr: string | null) => {
    return formatDateByPrefs(dateStr, dateFormat, timezoneOffset);
  }, [dateFormat, timezoneOffset]);

  // Table columns — 10 cols: Network/Company, Contact Name, Contact Email, Plan/Product, Status, KCs (used/max), Mailboxes (used/max), End Date, Created Date, Referral Code, Actions
  const columns = useMemo<ColumnDef<DeveloperPlanTenant>[]>(
    () => [
      {
        accessorKey: 'networkName',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_NetworkName', 'Network / Company')} />
        ),
        cell: ({ row }) => {
          const name = row.getValue('networkName') as string;
          const id = row.original.networkId;
          return `${name} (${id})`;
        },
      },
      {
        accessorKey: 'contactName',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_ContactName', 'Contact Name')} />
        ),
        cell: ({ row }) => (row.getValue('contactName') as string) || '—',
      },
      {
        accessorKey: 'contactEmail',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_ContactEmail', 'Contact Email')} />
        ),
        cell: ({ row }) => {
          const email = row.getValue('contactEmail') as string;
          return email
            ? <a href={`mailto:${email}`} className="text-primary hover:underline">{email}</a>
            : '—';
        },
      },
      {
        accessorKey: 'plan',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_Plan', 'Plan / Product')} />
        ),
      },
      {
        accessorKey: 'billingStatus',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_Status', 'Status')} />
        ),
        cell: ({ row }) => {
          const status = row.getValue('billingStatus') as string;
          const colorClass = statusColors[status?.toLowerCase()] ?? 'bg-gray-100 text-gray-800';
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
              {status}
            </span>
          );
        },
      },
      {
        accessorKey: 'kcs',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_Kcs', 'KCs')} />
        ),
        cell: ({ row }) => {
          const kcs = row.original.kcs ?? 0;
          const maxKcs = row.original.maxKcs;
          return maxKcs ? `${kcs} / ${maxKcs}` : `${kcs}`;
        },
      },
      {
        accessorKey: 'mailboxCreatedCount',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_Mailboxes', 'Mailboxes')} />
        ),
        cell: ({ row }) => {
          const used = row.original.mailboxCreatedCount ?? 0;
          const max = row.original.maxMailboxes;
          return max ? `${used} / ${max}` : `${used}`;
        },
      },
      {
        accessorKey: 'expireDate',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_EndDate', 'End Date')} />
        ),
        cell: ({ row }) => formatDate(row.getValue('expireDate')),
      },
      {
        accessorKey: 'beginDate',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_CreatedDate', 'Created Date')} />
        ),
        cell: ({ row }) => formatDate(row.getValue('beginDate')),
      },
      {
        accessorKey: 'referralCode',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('DeveloperPlan_Col_ReferralCode', 'Referral Code')} />
        ),
        cell: ({ row }) => (row.getValue('referralCode') as string) || '—',
      },
      {
        id: 'actions',
        header: () => (
          <div className="flex h-8 items-center justify-center text-sm font-semibold">
            {t('DeveloperPlan_Col_Actions', 'Actions')}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <button
              onClick={() => openSheet(row.original)}
              className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
            >
              {t('DeveloperPlan_Action_View', 'View')}
            </button>
          </div>
        ),
        enableSorting: false,
        size: 100,
      },
    ],
    [t, openSheet, statusColors, formatDate]
  );

  // Initialize table
  const table = useReactTable({
    data: data?.tenants ?? [],
    columns,
    state: {
      sorting,
      globalFilter,
    },
    getRowId: (row) => String(row.networkId),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = String(filterValue ?? '').toLowerCase().trim();
      if (!search) return true;
      const d = row.original;
      return (
        (d.networkName ?? '').toLowerCase().includes(search) ||
        (d.contactName ?? '').toLowerCase().includes(search) ||
        (d.contactEmail ?? '').toLowerCase().includes(search) ||
        (d.billingStatus ?? '').toLowerCase().includes(search) ||
        (d.referralCode ?? '').toLowerCase().includes(search) ||
        (d.tenant ?? '').toLowerCase().includes(search) ||
        (d.plan ?? '').toLowerCase().includes(search) ||
        (d.productName ?? '').toLowerCase().includes(search)
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // CSV export handler — exports filtered rows (what user currently sees)
  const handleExportCsv = useCallback(() => {
    const filteredRows = table.getFilteredRowModel().rows.map((r) => r.original);
    const csvColumns: CsvColumn<DeveloperPlanTenant>[] = [
      { header: t('DeveloperPlan_Col_NetworkName', 'Network / Company'), accessor: (r) => `${r.networkName} (${r.networkId})` },
      { header: t('DeveloperPlan_Col_ContactName', 'Contact Name'), accessor: (r) => r.contactName },
      { header: t('DeveloperPlan_Col_ContactEmail', 'Contact Email'), accessor: (r) => r.contactEmail },
      { header: t('DeveloperPlan_Col_Plan', 'Plan / Product'), accessor: (r) => r.plan },
      { header: t('DeveloperPlan_Col_Status', 'Status'), accessor: (r) => r.billingStatus },
      { header: t('DeveloperPlan_Col_Kcs', 'KCs'), accessor: (r) => r.kcs },
      { header: t('DeveloperPlan_Col_Mailboxes', 'Mailboxes'), accessor: (r) => r.mailboxCreatedCount },
      { header: t('DeveloperPlan_Col_EndDate', 'End Date'), accessor: (r) => r.expireDate },
      { header: t('DeveloperPlan_Col_CreatedDate', 'Created Date'), accessor: (r) => r.beginDate },
      { header: t('DeveloperPlan_Col_ReferralCode', 'Referral Code'), accessor: (r) => r.referralCode },
    ];
    const csv = convertToCsv(filteredRows, csvColumns);
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `developer-plans-${timestamp}.csv`);
  }, [table, t]);

  // Loading
  if (loading) {
    return <LoadingState loadingText={t('DeveloperPlan_Loading', 'Loading developer tenants...')} />;
  }

  // Error
  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={refetch}
        errorTitle={t('DeveloperPlan_Error_Title', 'Failed to load data')}
        retryText={t('DeveloperPlan_Error_Retry', 'Retry')}
      />
    );
  }

  if (!data) {
    return null;
  }

  return (
    <PortalLayout ref={ref} contentClassName="overflow-x-hidden">
      <div className="p-4 sm:p-6 space-y-4">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-accent">
              {t('DeveloperPlan_Title', 'Developer Plan Management')}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              {t(
                'DeveloperPlan_Description',
                'Manage Developer plan tenants — view billing status, extend trials.'
              )}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="h-auto w-full justify-start rounded-none border-b bg-transparent p-0">
            <TabsTrigger
              value="overview"
              className="rounded-none border-b-2 border-transparent px-4 py-2.5 font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              {t('DeveloperPlan_Tab_Overview', 'Developer Plans')}
            </TabsTrigger>
            <TabsTrigger
              value="sessions"
              className="rounded-none border-b-2 border-transparent px-4 py-2.5 font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              {t('DeveloperPlan_Tab_Sessions', 'Registration Sessions')}
            </TabsTrigger>
            <TabsTrigger
              value="simplify"
              className="rounded-none border-b-2 border-transparent px-4 py-2.5 font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              {t('SimplifyDeveloperPlan_Tab_Title', 'Simplify Developer Plans')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 pt-4">
            {/* Search + Export */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder={t('DeveloperPlan_Filter_Search', 'Search network, contact, status, referral code...')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {globalFilter && (
                <button
                  onClick={() => setGlobalFilter('')}
                  className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('DeveloperPlan_Filter_Clear', 'Clear')}
                </button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="px-4 py-2 text-sm border border-input bg-background rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
                >
                  {isSyncing && (
                    <div className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  )}
                  {isSyncing
                    ? t('DeveloperPlan_Action_Syncing', 'Syncing...')
                    : t('DeveloperPlan_Action_Sync', 'Sync Data')}
                </button>
                <button
                  onClick={handleExportCsv}
                  disabled={!data?.tenants?.length || isSyncing}
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {t('DeveloperPlan_ExportCsv', 'Export CSV')}
                </button>
              </div>
            </div>

            {/* Sync error banner — inline, preserves existing data */}
            {syncError && (
              <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center justify-between">
                <span>{t('DeveloperPlan_SyncError', 'Sync failed: {{message}}').replace('{{message}}', syncError.message)}</span>
                <button
                  onClick={syncData}
                  className="ml-4 text-xs underline hover:no-underline"
                >
                  {t('DeveloperPlan_Error_Retry', 'Retry')}
                </button>
              </div>
            )}

            {/* Data Table */}
            <div className="overflow-x-auto">
              <div className="min-w-max">
                <DataTable
                  table={table}
                  showPagination={true}
                  headerClassName="[&_th]:bg-gray-100 [&_th]:text-gray-700 dark:[&_th]:bg-gray-800 dark:[&_th]:text-gray-300"
                  noResultsText={t('DeveloperPlan_NoResults', 'No results found.')}
                  loadingText={t('DeveloperPlan_TableLoading', 'Loading...')}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sessions" className="pt-4">
            <RegistrationSessionsTab />
          </TabsContent>

          <TabsContent value="simplify" className="pt-4">
            <SimplifyDeveloperPlansTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Trial Extension Sheet */}
      <TrialExtensionSheet
        tenant={selectedTenant}
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
        onExtend={extendTrial}
        onConvert={convertToContract}
      />
    </PortalLayout>
  );
});

DeveloperPlanPage.displayName = 'DeveloperPlanPage';
